import {
  buildWhatsAppUrl, whatsappMessage, telHref,
  type HomepageConfig, type Transformation, type SiteSettings,
} from '@cng/shared';
import { BeforeAfterSlider } from '@/components/transformations/BeforeAfterSlider';

interface TransformationsProps {
  config: HomepageConfig['transformations'];
  items: Transformation[];
  settings: SiteSettings;
}

function ctaHref(item: Transformation, settings: SiteSettings): string {
  const phone = settings.whatsappNumber || settings.primaryPhone;
  switch (item.ctaType) {
    case 'phone':
      return telHref(item.ctaValue || settings.primaryPhone);
    case 'link':
      return item.ctaValue || '#';
    case 'none':
      return '';
    case 'whatsapp':
    default:
      return buildWhatsAppUrl({
        phone,
        message: item.ctaValue
          || whatsappMessage(
            'transformationEnquiry',
            { name: item.serviceName || item.title },
            settings.whatsappTemplates,
          ),
      });
  }
}

export function Transformations({ config, items, settings }: TransformationsProps) {
  if (!config?.enabled || items.length === 0) return null;

  return (
    <section className="testimonial-section" aria-labelledby="glow-up-title">
      <h2 className="section-title" id="glow-up-title">
        {config.title}
        {config.titleAccent ? <> <span>{config.titleAccent}</span></> : null}
      </h2>
      {config.subtitle ? <p className="section-subtitle">{config.subtitle}</p> : null}

      <div className="testimonials-grid">
        {items.map((item) => {
          const href = ctaHref(item, settings);
          const external = /^https?:/i.test(href);
          return (
            <article className="testimonial-card" key={item._id}>
              <BeforeAfterSlider
                beforeImage={item.beforeImage}
                afterImage={item.afterImage}
                labelBefore={item.labelBefore}
                labelAfter={item.labelAfter}
                stylistBadge={item.stylistBadge || (item.stylistName ? `Styled by ${item.stylistName}` : '')}
                initialPosition={item.initialPosition}
                title={item.title}
              />

              <div className="card-content">
                <h3 className="benefit-headline">{item.title}</h3>
                {item.verified && item.rating > 0 ? <div className="stars" aria-label={`Rated ${item.rating} out of 5`}>
                  <span aria-hidden="true">{'★'.repeat(Math.round(item.rating))}</span>
                </div> : null}
                {item.testimonial ? <p className="quote">{item.testimonial}</p> : null}
                <div className="client-meta">
                  <span className="client-name">{item.customerName}</span>
                  {item.verified && item.customerLabel ? <> — {item.customerLabel}</> : null}
                </div>
                {href && item.ctaLabel ? (
                  <a
                    className="cta-button"
                    href={href}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noopener noreferrer' : undefined}
                  >
                    {item.ctaLabel}
                  </a>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
